import type { RefObject } from 'react';
import gsap from 'gsap';
import { fadeTo } from 'util/animation/fade/fade';
import { revealRectangle } from '../../../../util/animation/reveal/reveal';
import { slideUp } from '../../../../util/animation/slide/slide';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import { findTransitionController } from '../../../../util/core-transition-component';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { VideoRef } from '../../../atoms/video/Video.data';

interface NewsOverviewItemRefs {
  elementRef: RefObject<HTMLElement | null>;
  imageHolderRef?: RefObject<HTMLDivElement | null>;
  lineRef?: RefObject<HTMLSpanElement | null>;
  categoryRef?: RefObject<HeadingRef | null>;
  titleRef?: RefObject<HeadingRef | null>;
  dateRef?: RefObject<HeadingRef | null>;
  imageRef?: RefObject<HTMLDivElement | null>;
  downloadLinkRef?: RefObject<PrimaryLinkRef | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  {
    elementRef,
    imageHolderRef,
    lineRef,
    categoryRef,
    titleRef,
    dateRef,
    imageRef,
    downloadLinkRef,
  }: NewsOverviewItemRefs,
): void {
  const categoryController = findTransitionController(categoryRef);
  const titleController = findTransitionController(titleRef);
  const dateController = findTransitionController(dateRef);
  const downloadController = findTransitionController(downloadLinkRef);

  if (imageHolderRef?.current) {
    timeline.add(
      slideUp(imageHolderRef.current, {
        from: 100,
        duration: 0.5,
      }),
      0,
    );
  }

  if (elementRef.current) {
    timeline.add(
      revealRectangle(elementRef.current, {
        from: 'bottom',
        duration: 0.5,
      }),
      0,
    );
  }

  if (imageRef?.current) {
    timeline.fromTo(
      imageRef.current,
      {
        scale: 1.3,
      },
      {
        scale: 1,
        clearProps: 'scale',
        ease: 'Quad.easeInOut',
        duration: 0.5,
      },
      0,
    );
  }

  if (categoryController) {
    timeline.add(categoryController.getTimeline('in') as gsap.core.Timeline, 0.2);
  }

  if (titleController) {
    timeline.add(titleController.getTimeline('in') as gsap.core.Timeline, 0.3);
  }

  if (dateController) {
    timeline.add(dateController.getTimeline('in') as gsap.core.Timeline, 0.6);
  }

  if (downloadController) {
    timeline.add(downloadController.getTimeline('in') as gsap.core.Timeline, 0.6);
  }

  if (lineRef?.current) {
    timeline.fromTo(
      lineRef?.current,
      { scaleX: 0 },
      {
        scaleX: 1,
        clearProps: 'scaleX',
        ease: 'Power2.easeOut',
        duration: 0.5,
      },
      0.4,
    );
  }
}

export function setupTransitionOutTimeline(
  timeline: gsap.core.Timeline,
  { elementRef }: NewsOverviewItemRefs,
): void {
  timeline.add(fadeTo(elementRef.current as HTMLElement));
}

export function scaleImage(
  imageRef?: RefObject<HTMLDivElement | ImageRef | VideoRef | null>,
  zoom = true,
  scaleValue = 1.1,
): void {
  if (imageRef?.current) {
    let element = imageRef?.current;
    if ((imageRef?.current as VideoRef)?.elementRef?.current) {
      element = (imageRef.current as VideoRef).elementRef.current as HTMLDivElement;
    }

    gsap.to(element, {
      scale: zoom ? scaleValue : 1,
      ease: 'Quad.easeInOut',
      duration: 0.5,
    });
  }
}

export function onHover(
  backgroundHoverRef: RefObject<HTMLDivElement | null>,
  isHover: boolean,
): void {
  if (isHover) {
    gsap.fromTo(
      backgroundHoverRef.current,
      {
        yPercent: isHover ? 100 : 0,
      },
      {
        yPercent: 0,
        duration: 0.5,
        ease: 'Power2.easeOut',
      },
    );
  } else {
    gsap.to(backgroundHoverRef.current, {
      yPercent: -100,
      duration: 0.5,
      ease: 'Power2.easeOut',
    });
  }
}
